import { memo, useMemo } from 'react';
import { remToPx, stripUnit } from 'polished';

import { focusHeadingOrContainer, tryCatch } from '../../utils';
import Button from '../Button';
import Flex from '../Flex';
import { useI18n, useShortcutManager, useTheme } from '../../hooks';
import useKeyboardCommands, { KeyboardShortcut } from '../KeyboardCommands';

import { StyledLinkWithShortcut, StyledSkipLinks, StyledSkipLinksItems } from './SkipLinks.styles';

interface SkipLink {
  label: string;
  target: { ariaLabel: string; selector: string };
}

export interface SkipLinksProps {
  items: SkipLink[];
}

const SkipLinks = memo(function SkipLinks({ items }: SkipLinksProps) {
  const bindings = useShortcutManager();
  const t = useI18n();
  const openKeyboardCommands = useKeyboardCommands();

  const {
    base: { spacing }
  } = useTheme();

  const padding = useMemo(
    () =>
      tryCatch(
        () => Number(stripUnit(remToPx(spacing))) * 3,
        () => 24
      ),
    [spacing]
  );

  return (
    <Flex
      as={StyledSkipLinks}
      portal={false}
      container={{ direction: 'column' }}
      placement='bottom-end'
      target={{
        getBoundingClientRect: () => ({
          height: 0,
          width: 0,
          x: padding,
          y: padding,
          top: padding,
          bottom: padding,
          left: padding,
          right: padding
        })
      }}
      modifiers={[
        {
          name: 'placeAndContain',
          enabled: false
        },
        {
          name: 'offset',
          options: {
            offset: [0, 0]
          }
        }
      ]}
      data-app-region
      aria-label={t('skip_navigation_menu_label')}
    >
      <Flex container={{ direction: 'column', gap: 0.5, pad: 1 }} as={StyledSkipLinksItems}>
        {items.map(item => (
          <Button
            variant='link'
            key={item.label}
            onClick={() =>
              focusHeadingOrContainer(
                document.querySelector(item.target.selector) ?? document,
                item.target.ariaLabel
              )
            }
          >
            {item.label}
          </Button>
        ))}

        {bindings.KeyboardCommands?.keyBinding && (
          <StyledLinkWithShortcut>
            <Button
              variant='link'
              key='Keyboard commands'
              onClick={() => {
                openKeyboardCommands();
              }}
            >
              {t('keyboard_commands')}
            </Button>

            <KeyboardShortcut keyBinding={bindings.KeyboardCommands?.keyBinding} />
          </StyledLinkWithShortcut>
        )}
      </Flex>
    </Flex>
  );
});

export default SkipLinks;
